import { Link } from 'react-router-dom'
import LegalLayout from './LegalLayout'

const BREADCRUMB = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://myschoolportal.xyz' },
    { '@type': 'ListItem', position: 2, name: 'Terms of Service', item: 'https://myschoolportal.xyz/terms' },
  ],
}

const SECTIONS = [
  {
    id: 'agreement',
    heading: 'The agreement',
    body: (
      <>
        <p>
          These Terms of Service (the &ldquo;<strong>Terms</strong>&rdquo;) govern access to and use of the
          MySchoolPortal platform (the &ldquo;<strong>Platform</strong>&rdquo;), operated by Tvantra
          (&ldquo;<strong>Tvantra</strong>&rdquo;, &ldquo;<strong>we</strong>&rdquo;, &ldquo;<strong>us</strong>&rdquo;).
        </p>
        <p>
          They form a binding agreement between Tvantra and the subscribing institution (the
          &ldquo;<strong>School</strong>&rdquo;). By creating a workspace or accessing the Platform, the School accepts
          these Terms. A person accepting on behalf of a School warrants that they are authorised to bind it.
        </p>
        <p>
          Where the School and Tvantra have executed a separate written agreement, that agreement prevails to the
          extent of any inconsistency.
        </p>
      </>
    ),
  },
  {
    id: 'service',
    heading: 'The service',
    body: (
      <>
        <p>
          Tvantra grants the School a non-exclusive, non-transferable right to access and use the Platform for the
          internal administration of the School, for the duration of its subscription and in accordance with these
          Terms.
        </p>
        <p>
          The modules available to a School, the number of permitted user accounts and any usage limits are those
          specified at the time of subscription. We may enhance or modify features from time to time, provided we do
          not materially diminish the core functionality subscribed to without notice.
        </p>
      </>
    ),
  },
  {
    id: 'accounts',
    heading: 'Accounts and security',
    body: (
      <>
        <p>
          The School is responsible for all activity conducted through its workspace, for the accounts it creates, and
          for the permissions it assigns to those accounts.
        </p>
        <p>
          Accounts are issued to named individuals and must not be shared. The School shall require its users to keep
          authentication credentials confidential and shall notify Tvantra without undue delay on becoming aware of any
          unauthorised access or suspected compromise.
        </p>
      </>
    ),
  },
  {
    id: 'data',
    heading: 'School data',
    body: (
      <>
        <p>
          As between the parties, all data entered into the Platform by or on behalf of the School
          (&ldquo;<strong>School Data</strong>&rdquo;) remains the property of the School. Tvantra acquires no ownership
          interest in it.
        </p>
        <p>
          Tvantra processes School Data solely to provide the Platform and as described in the{' '}
          <Link to="/privacy">Privacy Policy</Link>, which forms part of these Terms. We do not sell School Data or use
          it for advertising.
        </p>
        <p>
          The School warrants that it has the necessary authority to enter the personal data it records, including
          data relating to pupils and their guardians, and that it will do so in compliance with the Data Protection
          Act, 2012 (Act 843).
        </p>
      </>
    ),
  },
  {
    id: 'fees',
    heading: 'Fees and payment',
    body: (
      <>
        <p>
          Subscription charges, the applicable billing cycle (per term or per month) and the cost of SMS credits are
          those agreed with the School at the point of subscription. All charges are stated in Ghana Cedis unless
          otherwise agreed.
        </p>
        <p>
          SMS credits are consumed as messages are submitted for delivery and are not refundable once consumed. We may
          revise pricing on reasonable written notice, which will take effect from the start of the next billing
          cycle.
        </p>
        <p>
          Where charges remain unpaid after we have requested payment, we may suspend access in accordance with
          section 9.
        </p>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    heading: 'Acceptable use',
    body: (
      <>
        <p>The School shall not, and shall procure that its users do not:</p>
        <ul>
          <li>attempt to access any workspace, account or data belonging to another School;</li>
          <li>probe, scan, or test the vulnerability of the Platform, or circumvent any authentication or access control;</li>
          <li>upload malicious code, or any material that is unlawful or infringes the rights of a third party;</li>
          <li>use the SMS facility to transmit unsolicited bulk messages, or messages to recipients who have not consented to receive communications from the School;</li>
          <li>resell, sublicense or otherwise make the Platform available to any third party; or</li>
          <li>reverse engineer, decompile or attempt to derive the source code of the Platform, save to the extent such restriction is prohibited by law.</li>
        </ul>
        <p>
          Misuse of the SMS facility may additionally breach the rules of the relevant network operator or SMS
          provider, and may result in the suspension of that facility independently of these Terms.
        </p>
      </>
    ),
  },
  {
    id: 'availability',
    heading: 'Availability and support',
    body: (
      <>
        <p>
          We will use reasonable endeavours to keep the Platform available and to restore service promptly following
          an interruption. We do not warrant uninterrupted or error-free operation.
        </p>
        <p>
          Availability may be affected by scheduled maintenance, emergency maintenance, or the failure of a third-party
          provider. Where we schedule work likely to cause material interruption, we will give the School advance
          notice where reasonably practicable.
        </p>
      </>
    ),
  },
  {
    id: 'third-parties',
    heading: 'Third-party services',
    body: (
      <p>
        Certain functionality depends on third parties, including Paystack for payment processing and mNotify and
        Hubtel for SMS delivery. Use of those services is subject to the relevant provider&rsquo;s own terms. Tvantra is
        not liable for any act, omission, delay or failure of a third-party provider that is outside our reasonable
        control.
      </p>
    ),
  },
  {
    id: 'term',
    heading: 'Suspension and termination',
    body: (
      <>
        <p>The School may terminate its subscription at any time on notice to Tvantra.</p>
        <p>
          We may suspend or terminate access where the School commits a material breach of these Terms and fails to
          remedy it within a reasonable period after notice; where charges remain unpaid following a request for
          payment; or where continued provision would expose Tvantra or its users to material legal or security risk.
          Where practicable, we will give notice before suspending access.
        </p>
        <p>
          On termination, the School may request an export of School Data. Data will thereafter be deleted in
          accordance with the retention provisions of the <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </>
    ),
  },
  {
    id: 'warranties',
    heading: 'Warranties and disclaimers',
    body: (
      <>
        <p>
          We warrant that we will provide the Platform with reasonable skill and care. Except as expressly stated,
          the Platform is provided &ldquo;as is&rdquo; and all other warranties, whether express or implied, are
          excluded to the fullest extent permitted by law.
        </p>
        <p>
          The Platform reports on the data entered into it. Tvantra does not warrant the accuracy of any figure,
          balance, report or statement derived from data entered by the School or its users.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    heading: 'Limitation of liability',
    body: (
      <>
        <p>
          Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence, for
          fraud or fraudulent misrepresentation, or for any other liability that cannot lawfully be excluded.
        </p>
        <p>
          Subject to the foregoing, neither party is liable for indirect or consequential loss, loss of profit, loss of
          business, or loss of anticipated savings; and Tvantra&rsquo;s aggregate liability arising out of or in
          connection with these Terms is limited to the charges paid by the School in the twelve months preceding the
          event giving rise to the claim.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    heading: 'Changes to these Terms',
    body: (
      <p>
        We may amend these Terms from time to time. Where an amendment is material, we will notify Schools directly
        and, unless otherwise stated, it will take effect from the start of the next billing cycle. Continued use
        following the effective date constitutes acceptance.
      </p>
    ),
  },
  {
    id: 'general',
    heading: 'Governing law and general',
    body: (
      <>
        <p>
          These Terms are governed by the laws of the Republic of Ghana, and the courts of Ghana have exclusive
          jurisdiction over any dispute arising out of or in connection with them.
        </p>
        <p>
          If any provision is held invalid or unenforceable, the remaining provisions continue in full force. A failure
          to enforce any provision is not a waiver of it. These Terms, together with the{' '}
          <Link to="/privacy">Privacy Policy</Link>, constitute the entire agreement between the parties in relation to
          the Platform.
        </p>
        <p>
          Notices to Tvantra may be sent to{' '}
          <a href="mailto:info@myschoolportal.xyz">info@myschoolportal.xyz</a> or{' '}
          <a href="tel:+233596539060">+233 59 653 9060</a>.
        </p>
      </>
    ),
  },
]

export default function Terms() {
  return (
    <LegalLayout
      title="Terms of Service"
      intro="The agreement between Tvantra and the schools that subscribe to the MySchoolPortal platform."
      path="/terms"
      schema={[BREADCRUMB]}
      sections={SECTIONS}
    />
  )
}
